import { ArrowForwardIosSharp } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Divider, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { FormProvider, useForm } from 'react-hook-form';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getMemberEmailLog } from 'src/shared/services/memberService';
import { MemberEmailSearchFilter } from 'src/modules/invoice/models/memberEmailSearchFilter';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { MemberEmail } from '../models/memberEmail';

type MemberEmailLogsModalProps = {
  modalOpen: boolean;
  onClose: () => void;
  memberId: number;
  firstName: string;
  lastName: string;
  email?: string;
};

type EmailLogForm = {
  emailLogs: MemberEmail[];
};

export const MemberEmailLogsModal = (props: MemberEmailLogsModalProps) => {
  const { modalOpen, onClose, memberId, firstName, lastName, email } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [expandedPanel, setExpandedPanel] = useState<number | false>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const searchFilter: MemberEmailSearchFilter = useMemo(
    () => ({
      pageIndex,
      pageSize: 10,
      sortCol: 'mail_id',
      sortDir: 'desc',
      sent: true,
      circular_id: undefined,
      custom1: undefined,
      filter: '',
    }),
    [pageIndex],
  );

  const startItem = pageIndex * searchFilter.pageSize + 1;
  const endItem = Math.min((pageIndex + 1) * searchFilter.pageSize, totalItems);

  const formMethods = useForm<EmailLogForm>({
    mode: 'all',
    defaultValues: {
      emailLogs: [],
    },
  });

  const { reset, getValues } = formMethods;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getMemberEmailLog(memberId, searchFilter);

      if (isMounted()) {
        reset({ emailLogs: result.data.results });
        setTotalPages(result.data.totalPages);
        setTotalItems(result.data.totalItems);
      }
    } catch (err) {
      if (isMounted()) ConsoleHelper.log(err);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted, memberId, reset, searchFilter]);

  useEffect(() => {
    if (memberId) {
      fetchData();
    }
  }, [fetchData, memberId]);

  return (
    <GenericDialog
      open={modalOpen}
      onClose={() => onClose()}
      onSave={() => onClose()}
      padding={0}
      showDialogActions={false}
      dialogTitle={t('member:emailLog.title')}
      dialogSubTitle={`${firstName} ${lastName}`}
      dialogContent={
        <FormProvider {...formMethods}>
          <Grid container>
            <Grid item xs={12}>
              <LoadingOverlay loading={loading} />
              {email && (
                <Alert severity='info' sx={{ mx: 2, mt: 2 }}>
                  {t('gift:emailLogInfo', { email })}
                </Alert>
              )}
              <Box
                sx={{
                  '& .MuiAccordionSummary-root': {
                    flexDirection: 'row-reverse',
                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                      transform: 'rotate(90deg)',
                    },
                    '& .MuiAccordionSummary-content': {
                      ml: 1,
                    },
                  },
                  '& .MuiAccordion-root.Mui-expanded': {
                    borderBottom: '1px solid #e0e0e0',
                    boxShadow: '0 20px 15px -20px rgba(0, 0, 0, 0.1) inset !important',
                    margin: 0,
                  },
                }}
              >
                <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
                  {getValues('emailLogs').map((log, index) => (
                    <Accordion
                      disableGutters
                      elevation={0}
                      key={log.mail_id}
                      expanded={expandedPanel === log.mail_id}
                      onChange={(_, isExpanded) => {
                        setExpandedPanel(isExpanded ? log.mail_id : false);
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />}
                        aria-controls={`email-log-content-${log.mail_id}`}
                        id={`email-log-header-${log.mail_id}`}
                      >
                        <Box display='flex' flexDirection='row' justifyContent='space-between' width='100%'>
                          <Box>
                            <Typography sx={{ fontWeight: 500 }} mr={0.5}>
                              {log.subject}
                            </Typography>
                          </Box>

                          <Box display='flex' mr={2} flexDirection='row' justifyContent='flex-end' alignItems='center'>
                            <Typography sx={{ fontWeight: 500, alignItems: 'center' }} ml={1} mt={0.3} variant='body2' color='text.secondary'>
                              {DateHelper.getDateTime(log.circularExecutingTimeStamp)}
                            </Typography>
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails id={`email-log-content-${log.mail_id}`}>
                        <Box mb={3} sx={{ marginLeft: { xs: 0, sm: 5 }, marginRight: { xs: 0, sm: 5 } }}>
                          <Grid item xs={12} mt={2} mb={3}>
                            <ControlledTextField disabled name={`emailLogs[${index}].subject`} label={t('common:header')} />
                          </Grid>

                          <Grid item xs={12} mb={2}>
                            <ControlledTextEditor hideMenuBar hideToolbarSettings disabled name={`emailLogs[${index}].body`} />
                          </Grid>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </Box>
              <Divider />
              <Box display='flex' justifyContent='flex-end' alignItems='center' py={1} mt={1} pr={3} mb={1}>
                <Typography variant='body2' color='text.secondary' mr={2}>
                  {`${startItem}-${endItem} ${t('common:of')} ${totalItems}`}
                </Typography>
                <ArrowForwardIosSharp
                  sx={{
                    transform: 'rotate(180deg)',
                    cursor: pageIndex === 0 ? 'default' : 'pointer',
                    mr: 2,
                    color: pageIndex === 0 ? 'text.disabled' : 'primary.main',
                  }}
                  onClick={() => {
                    if (pageIndex > 0) setPageIndex((prev) => prev - 1);
                  }}
                />
                <ArrowForwardIosSharp
                  sx={{
                    cursor: pageIndex + 1 >= totalPages ? 'default' : 'pointer',
                    color: pageIndex + 1 >= totalPages ? 'text.disabled' : 'primary.main',
                  }}
                  onClick={() => {
                    if (pageIndex + 1 < totalPages) {
                      setPageIndex((prev) => prev + 1);
                    }
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </FormProvider>
      }
    />
  );
};
